import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { StoreResponse } from '@clarity/contract';
import { usePayouts } from '../../queries/index.js';
import { PayoutsView, SyncControl } from './shared.js';

/**
 * The standalone Payouts page for one onboarded store (clarity-orders-detail task
 * 4.1, Req 1.1–1.4). It is its OWN page at `/c/:clientId/<scope>/payouts` — no
 * `?tab=` tab host. The body is the single-store {@link PayoutsView}, framed by
 * the shared per-store header so "Sync now" stays available.
 *
 * This page owns THREE URL params in one `useSearchParams`:
 * - `?payout=` — the expanded payout's reconciliation detail, so a shared link /
 *   an order→payout trace (which navigates here from the Orders page) reopens it.
 * - `?from=` / `?to=` — the paid-at From/To filter range (`YYYY-MM-DD`); an absent
 *   bound means that side is open.
 *
 * Being the standalone surface, this page opts in to BOTH the paid-at filter
 * (`withFilter`) and the per-row Details affordance (`onOpenPayout`). All three
 * params are written atomically through a single {@link update} helper that starts
 * from the current params, so a change to one never clobbers its siblings
 * (payouts-monthly-view Req 2.2, 2.3, 3.3).
 */
export function StorePayouts({ store }: { store: StoreResponse }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openPayoutId = searchParams.get('payout');
  const ref = searchParams.get('ref'); // external payout ref from a notification deep-link
  const from = searchParams.get('from'); // null = no lower bound
  const to = searchParams.get('to'); // null = no upper bound

  // A notification "See the gap" deep-link arrives as `?ref=<externalPayoutId>`.
  // Resolve it to the payout's uuid and flip it into the `?payout=` expand (the
  // existing reconciliation-detail mechanism), then drop `?ref=` so a refresh is
  // idempotent. CRITICAL: wait until the payouts list has actually LOADED before
  // resolving — otherwise we'd clear `?ref=` against an empty list and lose it
  // before the data arrives. The list is a cheap cached read shared with PayoutsView.
  const payouts = usePayouts(store.id);
  const payoutsLoaded = payouts.data?.status === 200;
  useEffect(() => {
    if (!ref || !payoutsLoaded) return;
    const list = payouts.data?.status === 200 ? payouts.data.body : [];
    const match = list.find((p) => p.externalPayoutId === ref);
    const params = new URLSearchParams(searchParams);
    params.delete('ref');
    if (match) params.set('payout', match.id);
    setSearchParams(params, { replace: true });
    // Only re-run when the ref or the loaded list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, payoutsLoaded]);

  // Atomic multi-param write: start from the CURRENT params so unmentioned
  // siblings are preserved, then set/delete only the keys in `next`. This is why
  // toggling `?payout=` never drops `?from=`/`?to=` and vice-versa.
  function update(next: Partial<{ payout: string | null; from: string | null; to: string | null }>) {
    const params = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v) params.set(k, v);
      else params.delete(k);
    }
    setSearchParams(params);
  }

  return (
    <section>
      <SyncControl store={store} />
      <PayoutsView
        store={store}
        withFilter
        openPayoutId={openPayoutId}
        onTogglePayout={(id) => update({ payout: id })}
        from={from}
        to={to}
        onRangeChange={(f, t) => update({ from: f, to: t })}
        // Opt-in SIGNAL only: its presence turns on the panel's per-row Details
        // affordance. PayoutsView owns the externalPayoutId→raw id mapping and
        // calls onTogglePayout itself, so this body is never invoked — a no-op
        // is correct here.
        onOpenPayout={() => {}}
      />
    </section>
  );
}
